import { InflateRequestMode } from '@sccs/types';
import { ApolloClient, createHttpLink, InMemoryCache, NormalizedCacheObject } from 'sccs-shared-apollo-client-v0';
import { RequestHeaderConfiguration } from '../../../../types/ApiClientConfig';
import { MarketConfiguration, SharedRequestConfiguration } from '../../../../types/GeneralParameter';
import { Query, QuerySearchArgs } from '../../../../types/one-search/queries';
import { isCommercialVanByMode } from '../../../../util/VansConfiguration';
import {
  getRequestGraphQLConfiguration,
  removeClassSuffixForOneSearch,
  removeClassSuffixFromMultipleIdentifier,
} from '../../../../util/one-search/RequestConfiguration';
import {
  filterBodyTypes,
  filterFuelTypeHarmonized,
  filterVehicleClasses,
} from '../../../../util/one-search/VehicleClassTypeGuard';
import { ModeSpecificFetchClient } from '../../../GenericFetchClient';
import { GET_SEARCH_RESULTS } from './queries';
export type OneSearchClient = ApolloClient<NormalizedCacheObject>;

export type OneSearchTileDataResponseWithId = Query & {
  responseTypeId: 'OneSearchVehicleTileDataQuery';
};

export type OneSearchVehicleTileDataParams = Omit<QuerySearchArgs, 'modelIdentifier' | 'bodyType' | 'fuelType'> &
  MarketConfiguration & {
    modelIdentifier?: string;
    bodyType?: string;
    fuelType?: string;
  };

export class OneSearchGraphQlClient implements ModeSpecificFetchClient {
  private parameters?: QuerySearchArgs;
  private config?: RequestHeaderConfiguration;
  constructor(config: RequestHeaderConfiguration) {
    this.config = config;
  }

  getInflateIdLength(): number {
    throw new Error('Method not implemented.');
  }
  setMode(): void {
    throw new Error('Method not implemented.');
  }

  setRequiredParameters(parameters: OneSearchVehicleTileDataParams, configuration: SharedRequestConfiguration): void {
    const additionalRequestConfig = this.getAdditionalRequestConfig(parameters, configuration);
    const graphQlParamters: QuerySearchArgs = {
      ...additionalRequestConfig,
      modelIdentifier: filterVehicleClasses(additionalRequestConfig.modelIdentifier ?? ''),
      bodyType: filterBodyTypes(additionalRequestConfig.bodyType ?? ''),
      fuelType: filterFuelTypeHarmonized(additionalRequestConfig.fuelType ?? ''),
    };
    this.parameters = graphQlParamters;
  }

  private getAdditionalRequestConfig(
    parameter: OneSearchVehicleTileDataParams,
    configuration: SharedRequestConfiguration,
  ) {
    const classFilters = this.getClassFilterByMode(configuration.mode, parameter.modelIdentifier);
    const isVan = isCommercialVanByMode(configuration.mode);

    return getRequestGraphQLConfiguration(isVan, { ...parameter, modelIdentifier: classFilters });
  }

  private getClassFilterByMode(mode: string, classFilter: string | undefined): string {
    if (!classFilter) return '';
    switch (mode) {
      case InflateRequestMode.PI_PAGES_VANS:
        return classFilter; // VITO_TOURER won't be splitted here
      case InflateRequestMode.PI_PAGES_CARS:
        return removeClassSuffixForOneSearch(classFilter);
      case InflateRequestMode.HOMEPAGE_CARS:
      case InflateRequestMode.HOMEPAGE_VANS:
      case InflateRequestMode.CAMPAIGN_TEASER_CARS:
      case InflateRequestMode.CAMPAIGN_TEASER_VANS:
        return removeClassSuffixFromMultipleIdentifier(classFilter);
      default:
        throw new Error('Unknown Teaser Mode');
    }
  }

  async getResponse(): Promise<OneSearchTileDataResponseWithId> {
    const httpLink = createHttpLink({
      uri: this.config?.basePath,
      headers: {
        'x-api-key': this.config?.apiKey || '',
      },
    });

    const cache = new InMemoryCache({
      addTypename: false,
    });

    const oneSearchClient = new ApolloClient({
      link: httpLink,
      cache,
    });

    const gqlQueryParams = {
      variables: {
        ...this.parameters,
      },
      query: GET_SEARCH_RESULTS,
    };

    const oneSearchResponse = await oneSearchClient.query<Query>({
      ...gqlQueryParams,
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return {
      responseTypeId: 'OneSearchVehicleTileDataQuery',
      search: {
        results: oneSearchResponse?.data?.search?.results || [],
      },
    };
  }

  checkAllRequiredParametersPresent(): boolean {
    return true;
  }
}
